<template>
  <div id="app" ref="infoBox">
    <div class="app-container" :class="{'start': page > 0}">
      <!-- Logo -->
      <div class="text-center mb-5 mt-3" v-if="page >= 0">
        <img alt="Winefamly" src="@/assets/logo.svg" />
      </div>

      <!-- Progress bar -->
      <div class="my-3 my-sm-5 steps" v-if="page > 0">
        <span class="float-left cursor-pointer back-icon" @click="backOnePage">
          <font-awesome-icon icon="reply" size="lg" />
        </span>
        <ul v-bind:class="stepProgress">
          <div
            class="line"
            :style="{'background-image': 'linear-gradient(90deg, rgba(2,87,68,1)' + ((page - 1) / (questions.length - 1)) * 100 + '%, rgba(222,222,222,1)' + ((page - 1) / (questions.length - 1)) * 100 + '%, rgba(222,222,222,1) 100%)'}"
          ></div>
          <li
            :class="{'active': page == index + 1, 'completed': page > index + 1}"
            v-for="(question, index) in questions"
            :key="index"
          >{{ index + 1 }}</li>
        </ul>
      </div>

      <!-- Last page of the quiz -->
      <div
        class="mt-4 text-center"
        v-if="page == questions.length + 1 && loaded && questions.length"
      >
        <h3 v-translate>That sounds delicious!</h3>
        <span v-translate>Complete the course with the right wine</span>

        <h1 class="mt-5" v-if="!submitSuccessful">
          <font-awesome-icon icon="spinner" size="lg" class="fa-spin" />
        </h1>

        <div class="mt-5" v-if="submitSuccessful">
          <b-button variant="success" @click="seeResults" v-translate>See our recommendations</b-button>
        </div>

        <div class="mt-5">
          <p class="font-italic" v-translate>or...</p>
          <b-button variant="outline" @click="goToStart" v-translate>Take the test again</b-button>
        </div>
      </div>

      <!-- OPENING QUESTIONS -->
      <div class="mt-4 text-center">
        <div class="mb-4" v-if="page == 0">
          <h1 class="intro-headline" v-translate>What is on the menu?</h1>
          <h4 class="intro-subhead" v-translate>Let us help you find the perfect wine</h4>
        </div>

        <div v-if="page == 0" class="row justify-content-center align-items-start">
          <div
            class="card-container ml-sm-2 mr-2 mb-2 mt-2 show mobile-layout mobile-rectangle"
            v-for="(question, index) in openingQuestions"
            v-bind:key="index"
          >
            <div
              class="card"
              v-if="question"
              :class="{'selected': question.selected}"
              @click="handleQuestion(openingQuestions, question, index)"
            >
              <div class="card-body">
                <div class="image-holder">
                  <img alt="Answer" :src="question.imageUrl" />
                </div>

                <h5 class="answer-text">{{ question.text }}</h5>
              </div>
            </div>
          </div>
        </div>

        <div v-if="page == 0 && showStartButton" class="mt-3">
          <b-button :class="{ 'btn-fixed': actionsFixed }" variant="danger" @click="startQuiz">Start</b-button>
        </div>
      </div>

      <!-- QUESTIONS -->
      <div class="mt-4 text-center" v-bind:key="index" v-for="(question, index) in questions">
        <div v-if="page == index + 1">
          <h4>{{ question.text }}</h4>
          <p v-if="question.description">{{ question.description }}</p>

          <div class="mt-5 mb-4 row justify-content-center align-items-start">
            <div
              v-if="!questions.some(q => q.answers.some(a => a.selected == true && answer.excludeForAnswers.includes(a.id)))"
              class="card-container ml-sm-2 mr-2 mb-2 mt-2 mobile-layout"
              v-for="(answer, answerIndex) in question.answers"
              v-bind:key="answerIndex"
              :class="{
                'mobile-rectangle': question.answers.length < 4, 
                'mobile-square': question.answers.length >= 4, 
                'show': loaded,
                'prices': question.text === 'Hvad synes du en vin til menuen bør koste?'
                }"
            >
              <div
                class="card"
                :class="{'selected': answer.selected}"
                @click="handleAnswer(answer, question, index)"
              >
                <div class="card-body">
                <div v-if="question.answerType == 'multiple'" class="multiselect"></div>
                  <div class="image-holder">
                    <img alt="Answer" :src="answer.imageUrl" />
                  </div>

                  <h5 v-if="question.text !== 'Hvad synes du en vin til menuen bør koste?'" class="answer-text">{{ answer.text }}</h5>
                </div>
              </div>

              <br />

              <div class="counter" v-if="question.answerType == 'counter'">
                <font-awesome-icon
                  icon="minus"
                  class="cursor-pointer"
                  @click="reduceCounter($event, answer, question, index, answerIndex)"
                />
                <input type="text" class="counter-input" disabled v-model="answer.counter" />
                <font-awesome-icon
                  icon="plus"
                  class="cursor-pointer"
                  @click="increaseCounter($event, answer, question, index, answerIndex)"
                />
              </div>
            </div>
          </div>

          <div class="mt-3 btn-fixed" v-if="question.showNextButton">
            <p class="text-danger" v-if="question.errorNextButton">
              <span v-translate>You can pick a maximum of</span>
              {{ question.maximumAnswersAllowed }}
              <span v-translate>options</span>
            </p>
            <b-button
              :class="{ error: question.errorNextButton }"
              variant="danger"
              @click="forwardOnePage"
              v-translate
            >Next question</b-button>
          </div>

          <div class="mt-2" v-if="question.answerType == 'counter'">
            <span>{{ question.allCounters }}</span>
            /
            <span>{{ question.minimumAnswersRequired }}</span>
            chosen
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import our custom Http request instance (using Axios)
import winefamlyHttp from "@/util/configureAxios.js";

function initializeData() {
  return {
    openingQuestions: [],
    loaded: false,
    showStartButton: false,
    selectedQuizId: "",
    quizId: "",
    questions: [],
    answers: [],
    submitSuccessful: false,
    responseID: "",
    page: 0,
    actionsFixed: false,
  };
}

export default {
  name: "Home",
  data: initializeData,
  mounted: function() {
    this.$translate.setLang("da_DK");
  },
  computed: {
    stepProgress() {
      return "step-" + this.page;
    }
  },
  methods: {
    resetData() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    loadData() {
      var self = this;

      winefamlyHttp
        .get("/openingquestions")
        .then(function(response) {
          // handle success
          self.openingQuestions = response.data;
          self.loaded = true;

          if (self.$route.params.quizId !== undefined) {
            let index = self.openingQuestions.findIndex(
              p => p.quizId == self.$route.params.quizId
            );
            self.handleQuestion(
              self.openingQuestions,
              self.openingQuestions[index],
              index
            );
            self.startQuiz();
          }
        })
        .catch(function(error) {
          // handle error
          console.log(error);
        });
    },
    handleQuestion(questions, question, index) {
      var self = this;
      // If question requires only one answer
      questions.forEach(function(item) {
        item.selected = false;
      });

      this.selectedQuizId = question.quizId;

      questions[index].selected = true;
      this.openingQuestions = [];
      questions.forEach(function(item) {
        self.openingQuestions.push(item);
      });

      this.showStartButton = true;
      this.setButtonFixed();
    },
    handleAnswer(answer, question, index) {
      let self = this;
      let answers = question.answers;

      if (question.answerType == "multiple") {
        // If question requires multiple answers

        let numberOfSelected = 0;

        if (answer.selected) {
          answer.selected = false;
        } else {
          answer.selected = true;
        }

        // we have no other way to check if the selected answer is
        // "I don't know", so we're doing this comparison
        // and unselecting all the other answers
        const dontKnowText = "Ikke Sikker";

        if (answer.text == dontKnowText && answer.selected == true) {
          answers
            .filter(item => item.text != dontKnowText)
            .map(item => (item.selected = false));
        }

        if (answer.text != dontKnowText) {
          answers.find(item => item.text == dontKnowText).selected = false;
        }

        this.$set(this.questions, index, question);

        // Count number of selected answers
        answers.forEach(function(item) {
          if (item.selected) {
            numberOfSelected += 1;
          }
        });

        // Show next question button only if satisfies requirements from the API
        if (
          numberOfSelected >= question.minimumAnswersRequired &&
          numberOfSelected <= question.maximumAnswersAllowed
        ) {
          question.showNextButton = true;
          question.errorNextButton = false;
          this.$set(this.questions, index, question);
        } else if (numberOfSelected > question.maximumAnswersAllowed) {
          question.showNextButton = true;
          question.errorNextButton = true;
          this.$set(this.questions, index, question);
        } else {
          question.showNextButton = false;
          question.errorNextButton = false;
          this.$set(this.questions, index, question);
        }
      } else {
        // If question requires only one answer
        answers.forEach(function(item) {
          item.selected = false;
        });
        answer.selected = true;

        setTimeout(function() {
          self.forwardOnePage();
        }, 200);
      }
    },
    startQuiz() {
      var self = this;
      this.page = 1;
      winefamlyHttp
        .get(this.selectedQuizId)
        .then(function(response) {
          // handle success
          self.questions = response.data.questions;
          self.quizId = response.data.id;
          self.loaded = true;

          window.dataLayerHD = window.dataLayerHD || []
          window.dataLayerHD.push({
            event: 'FoodnwineQuizStep',
            eventCategory: 'FoodnwineQuiz',
            eventAction: 'Saw Step',
            eventLabel: 'FoodnwinePage' + (self.page + 1)
          });

        })
        .catch(function(error) {
          // handle error
          console.log(error);
        });
    },
    postAnswers() {
      // keep reference to this object to use it in asynchronous functions
      let self = this;

      winefamlyHttp
        .post("/", {
          quizId: this.quizId,
          answers: this.answers
        })
        .then(function(response) {
          if (response.data) {
            setTimeout(function() {
              self.submitSuccessful = true;
              self.responseID = response.data;
            }, 300);
          }
        })
        .catch(function(error) {
          if (error) {
            self.showError = true;
            self.errorMessage = error;
          }
        });
    },
    seeResults() {
      // Function that redirects to the shop page with param received from the API
      var resultsBaseUrl = process.env.VUE_APP_RESULTS_URL ?? document.referrer;
      let url =
        resultsBaseUrl + "/foodandwine/result/?resultid=" + this.responseID;
      window.location.href = url;
    },
    goToStart() {
      this.resetData();
      this.loadData();
    },
    // Previous question (reduce page by 1)
    backOnePage() {
      this.page -= 1;
      if (this.page == 0) {
        this.goToStart();
      }
    },
    // Previous question (reduce page by 1)
    forwardOnePage() {
      let self = this;

      this.loaded = false;

      setTimeout(function() {
        self.loaded = true;
      }, 300);

      let selectedAnswers = [];
      this.page += 1;

      window.dataLayerHD = window.dataLayerHD || []
      window.dataLayerHD.push({
        event: 'FoodnwineQuizStep',
        eventCategory: 'FoodnwineQuiz',
        eventAction: 'Saw Step',
        eventLabel: 'FoodnwinePage' + (self.page + 1)
      });

      // If current page is greater than number of questions
      if (this.page == this.questions.length + 1) {
        this.questions.forEach(function(question) {
          selectedAnswers = [];
          question.answers.forEach(function(answer) {
            if (answer.selected) {
              selectedAnswers.push(answer.id);
            }
            if (answer.counter) {
              for (let i = 0; i < answer.counter; i++) {
                selectedAnswers.push(answer.id);
              }
            }
          });

          self.answers.push({
            questionId: question.id,
            answerIds: selectedAnswers
          });
        });

        // Post answers to the API
        this.postAnswers();
      }
    },
    setButtonFixed() {
      // Function that checks whether the content exceeds window height, and sets fixed positon for next buttons
      let self = this,
        appHeight = this.$refs.infoBox.clientHeight;
      if (appHeight >= window.innerHeight) {
        self.actionsFixed = true;
      } else {
        self.actionsFixed = false;
      }
    },
  },
  created() {
    this.loadData();

    window.dataLayerHD = window.dataLayerHD || []
    window.dataLayerHD.push({
      event: 'FoodnwineQuizStep',
      eventCategory: 'FoodnwineQuiz',
      eventAction: 'Saw Step',
      eventLabel: 'FoodnwinePage1'
    });
  }
};
</script>
